<template>
  <NumberFieldRoot
    v-model="modelValue"
    :class="[
      `design-system-input flex w-full items-center rounded-lg border transition`,
      `outline-2 -outline-offset-1 focus-within:outline`,
      isDisabled && `opacity-50`,
      fieldContext?.validationStatus.value === 'error'
        ? `border-red-300 outline-red-500 dark:border-red-800`
        : `outline-blue-500`,
    ]"
    :min="min"
    :max="max"
    :step="step"
    :disabled="isDisabled"
    :format-options="formatOptions"
    :locale="locale"
  >
    <NumberFieldInput
      :class="[
        `block w-full border-none bg-transparent px-2.5 py-1.5 text-sm text-current placeholder:text-muted-foreground focus:outline-none focus:ring-0 disabled:bg-transparent disabled:text-current`,
      ]"
      :placeholder="placeholder"
      :required="fieldContext?.required.value"
      :aria-invalid="fieldContext?.validationStatus.value === 'error'"
      :aria-describedby="ariaDescribedBy"
    />

    <div class="flex shrink-0">
      <NumberFieldDecrement
        class="flex size-8 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground active:text-foreground disabled:opacity-30"
      >
        <MinusIcon class="size-4" />
      </NumberFieldDecrement>

      <NumberFieldIncrement
        class="flex size-8 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground active:text-foreground disabled:opacity-30"
      >
        <PlusIcon class="size-4" />
      </NumberFieldIncrement>
    </div>
  </NumberFieldRoot>
</template>

<script lang="ts" setup>
import {
  NumberFieldDecrement,
  NumberFieldIncrement,
  NumberFieldInput,
  NumberFieldRoot,
} from "reka-ui";
import { computed } from "vue";

import MinusIcon from "@/icons/heroicons/minus-micro.svg";
import PlusIcon from "@/icons/heroicons/plus-micro.svg";

import { injectFieldContext } from "./context";

const modelValue = defineModel<number>({ default: 0 });

const props = defineProps<{
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  disabled?: boolean;
  formatOptions?: Intl.NumberFormatOptions;
  locale?: string;
}>();

const fieldContext = injectFieldContext(null);

const isDisabled = computed(() => {
  return fieldContext?.disabled.value || props.disabled;
});

const ariaDescribedBy = computed(() => {
  if (!fieldContext) {
    return undefined;
  }

  const ids: string[] = [
    fieldContext.descriptionId,
    fieldContext.hintId,
    fieldContext.validationMessageId,
  ];

  return ids.join(" ");
});
</script>
